import img1 from "../../assets/Images/Procurement/Procurement Purchases.png"
import img2 from "../../assets/Images/Procurement/Procurement Purchases Add Purchases.png"
import img3 from "../../assets/Images/Procurement/Purchase generated invoice.png"

export default function Purchases() {
    return (
        <section id="purchases" className="space-y-16">

            {/* SECTION TITLE */}
            <section className="bg-white rounded-xl border border-slate-200 p-10">
                <h1 className="text-2xl font-bold text-slate-900 mb-2">
                    2. Purchases
                </h1>
                <p className="text-md text-slate-600 max-w-3xl">
                    The Purchases module is used to record goods or services bought from
                    suppliers, track outstanding balances, and manage supplier invoices.
                </p>


                {/* OVERVIEW */}
                <section className="bg-white border-l border-[#C9A24E] p-4 mt-2">
                    <h2 className="text-lg font-semibold mb-3">
                        Overview
                    </h2>
                    <p className="text-slate-600 text-sm">
                        Purchases represent all procurement transactions made with suppliers.
                        Each purchase maintains a complete record of items, quantities, prices,
                        payments received, and remaining balances.
                    </p>
                </section>
            </section>

            {/* PURCHASE LIST */}
            <section className="">
                <div>
                    <h3 className="font-semibold mb-2">
                        Purchases List
                    </h3>

                    <p className="text-slate-600 mb-4">
                        When the Purchases module is opened, a list of all recorded purchases
                        is displayed in a tabular format.
                    </p>

                    <ul className="list-disc pl-5 text-slate-600 space-y-1">
                        <li>Supplier name</li>
                        <li>Purchase date</li>
                        <li>Total purchase amount</li>
                        <li>Received (paid) amount</li>
                        <li>Remaining balance</li>
                        <li>Status (Due / Paid)</li>
                        <li>Actions (Invoice, Edit, Payment Receipt, Delete)</li>
                    </ul>
                </div>

                <div className=" rounded-lg p-4  mt-4">
                    <img src={img1} alt="" />
                </div>
            </section>

            {/* ADD PURCHASE */}
            <section className="bg-white border border-slate-200 rounded-xl p-8">
                <h3 className="font-semibold mb-2">
                    Creating a New Purchase
                </h3>

                <p className="text-slate-600 mb-4">
                    Clicking the <strong>Add New</strong> button opens the Purchase Form
                    where a new supplier purchase can be recorded.
                </p>

                <ul className="list-disc pl-5 text-slate-600 space-y-1">
                    <li>Select Supplier</li>
                    <li>Purchase Date</li>
                    <li>Status (Due / Paid)</li>
                    <li>Payment Method (Cash, Bank, etc.)</li>
                    <li>Optional purchase note</li>
                </ul>

                <div className="bg-slate-100 rounded-lg p-4 text-sm text-slate-500 mt-4">
                    <img src={img2} alt="" />
                </div>
            </section>

            {/* ITEMS HANDLING */}
            <section className="bg-slate-100 rounded-xl p-8">
                <h3 className="font-semibold mb-2">
                    Managing Purchase Items
                </h3>

                <p className="text-slate-600 mb-4">
                    Each purchase can contain one or multiple items. The item table allows
                    dynamic item management.
                </p>

                <ul className="list-disc pl-5 text-slate-600 space-y-1">
                    <li>Add multiple items to a single purchase</li>
                    <li>Enter item name, quantity, and unit price</li>
                    <li>Item total is calculated automatically</li>
                    <li>Items can be removed before saving</li>
                </ul>

                <p className="text-slate-600 mt-3">
                    The system automatically calculates the <strong>Grand Total</strong>
                    based on all added items.
                </p>
            </section>

            {/* FINANCIAL CALCULATION */}
            <section className="bg-white border border-slate-200 rounded-xl p-8">
                <h3 className="font-semibold mb-2">
                    Payment & Balance Calculation
                </h3>

                <p className="text-slate-600 mb-4">
                    EventFlow calculates outstanding balances in real time based on the
                    received amount.
                </p>

                <ul className="list-disc pl-5 text-slate-600 space-y-1">
                    <li>Grand Total (sum of all items)</li>
                    <li>Received Amount (paid to supplier)</li>
                    <li>Remaining Balance (auto-calculated)</li>
                </ul>
            </section>

            {/* PURCHASE STATUS LOGIC */}
            <section className="bg-slate-100 rounded-xl p-8">
                <h3 className="font-semibold mb-2">
                    Purchase Status Logic
                </h3>

                <p className="text-slate-600 mb-4">
                    Purchase status is managed automatically based on payment activity.
                </p>

                <ul className="list-disc pl-5 text-slate-600 space-y-1">
                    <li>Status is <strong>Due</strong> when balance is greater than zero</li>
                    <li>Status changes to <strong>Paid</strong> when balance becomes zero</li>
                    <li>Status updates automatically when purchase payments are added or edited</li>
                </ul>
            </section>


            {/* SAVE & INVOICE */}
            <section className="bg-slate-100 rounded-xl p-8">
                <h3 className="font-semibold mb-2">
                    Saving the Purchase & Invoice Generation
                </h3>

                <p className="text-slate-600 mb-4">
                    Once the purchase is saved, the system automatically generates a
                    supplier invoice.
                </p>

                <ul className="list-disc pl-5 text-slate-600 space-y-1">
                    <li>Unique invoice number is assigned</li>
                    <li>Supplier and item details are locked</li>
                    <li>Invoice status reflects payment state</li>
                </ul>

                <div className="rounded-lg p-4 mt-4">
                    <img src={img3} alt="" className="w-[700px] h-auto" />
                </div>
            </section>

            {/* INVOICE DETAILS */}
            <section className="bg-white border border-slate-200 rounded-xl p-8">
                <h3 className="font-semibold mb-2">
                    Purchase Invoice
                </h3>

                <p className="text-slate-600 mb-4">
                    The purchase invoice displays complete transaction details including:
                </p>

                <ul className="list-disc pl-5 text-slate-600 space-y-1">
                    <li>Supplier information</li>
                    <li>Invoice number and print date</li>
                    <li>Item-wise breakdown</li>
                    <li>Grand total, received amount, and balance</li>
                    <li>Optional notes</li>
                </ul>

                <p className="text-slate-600 mt-3">
                    A <strong>Print Invoice</strong> button is available for physical
                    records or sharing.
                </p>
            </section>

            {/* PURCHASE PAYMENTS LINK */}
            <section className="bg-white border border-slate-200 rounded-xl p-8">
                <h3 className="font-semibold mb-2">
                    Recording Payments Against Purchases
                </h3>

                <p className="text-slate-600">
                    Supplier payments are recorded through the <strong>Purchase Payments</strong>
                    module. Each payment updates the received amount, remaining balance, and
                    status of the purchase automatically.
                </p>
            </section>


            {/* ACTIONS */}
            <section className="bg-slate-100 rounded-xl p-8">
                <h3 className="font-semibold mb-2">
                    Available Actions
                </h3>

                <ul className="list-disc pl-5 text-slate-600 space-y-1">
                    <li>
                        <strong>Invoice:</strong> View or print the purchase invoice
                    </li>
                    <li>
                        <strong>Edit:</strong> Modify purchase details before final settlement
                    </li>
                    <li>
                        <strong>Payment Receipt:</strong> Record supplier payments
                    </li>
                    <li>
                        <strong>Delete:</strong> Remove incorrect or duplicate purchases
                    </li>
                </ul>
            </section>

            {/* WHY IMPORTANT */}
            <section className="bg-slate-100 rounded-xl p-8">
                <h3 className="font-semibold mb-2">
                    Why Purchase Management Matters
                </h3>

                <p className="text-slate-600 max-w-3xl">
                    Proper purchase tracking ensures accurate supplier balances, prevents
                    financial discrepancies, and provides complete visibility into
                    procurement expenses.
                </p>
            </section>

        </section>
    );
}
